// src/components/EditUOMModal.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faCheck, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { decryptData } from '../../utils/storageCrypto';
import { Spinner } from '../Loader/Loader'; // Adjust path

const EditUOMModal = ({ 
  isOpen, 
  onClose, 
  uomData, 
  onUpdateSuccess,
  fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET 
}) => {
  const [uom, setUom] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  // Load existing data when modal opens
  useEffect(() => {
    if (isOpen && uomData) {
      setUom(uomData.UOM || '');
      setDescription(uomData.UOM_description || '');
      setErrorMessages([]);
    }
  }, [isOpen, uomData]);

  const handleClose = () => {
    setUom('');
    setDescription('');
    setErrorMessages([]);
    onClose();
  };

  const validateForm = () => {
    const errors = [];
    if (!uom?.trim()) errors.push('UOM is required');
    return errors;
  };

  const handleUpdateUOM = async () => {
  const errors = validateForm();
  if (errors.length > 0) {
    setErrorMessages(errors);
    return;
  }

  setIsSaving(true);
  try {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    const response = await fetch(`${fetchUrl}UOMs/update-UOM`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify({
        _id: uomData._id,
        UOM: uom.trim()
      }),
    });

    const data = await response.json();

    if (data.status === 200 || data.status === 201) {
    onUpdateSuccess?.(data.data, 'UOM updated successfully');
      handleClose();
    } else {
      setErrorMessages([data.message || 'Update failed']);
    }
  } catch (error) {
    console.error('Update UOM error:', error);
    setErrorMessages(['Internal Server Error. Please try again.']);
  } finally {
    setIsSaving(false);
  }
};

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 z-50 flex items-top justify-center bg-gray-800 bg-opacity-50">
    
    <div className="bg-white rounded-b-md shadow-md w-1/2 h-2/3 flex flex-col animate-slide-in">

      {/* Header */}
      <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-[10%] px-6'>
        <h2 className="text-md">Edit UOM</h2>
        <button onClick={handleClose}>
          <FontAwesomeIcon icon={faX} className="text-xs text-[#e54643] cursor-pointer" />
        </button>
      </div>

      <hr className="border-t border-gray-200 w-full" />

      {/* Errors */}
      {errorMessages.length > 0 && (
        <div className='p-1'>
          <div className="mt-1 flex justify-between items-center py-1 bg-[#fddcde] rounded-md">
            <div className="px-6 flex flex-col">
              {errorMessages.map((error, index) => (
                <span key={index} className="text-sm">
                  • {error}
                </span>
              ))}
            </div>
            <div className='px-4'>
              <button onClick={() => setErrorMessages([])}>
                <FontAwesomeIcon icon={faX} className="text-xs text-[#F7525A]" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5 px-6">
        <label className="block text-sm text-[#e54643] w-1/6">
          UOM<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={uom}
          onChange={(e) => setUom(e.target.value)}
          className="w-[40%] outline-none rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 p-2 text-sm"
        />
      </div>

      <div className="w-full flex flex-row items-center mb-4 space-x-2 px-6">
        <label className="block text-sm text-gray-700 w-1/6">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-[40%] outline-none rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 p-2 text-sm"
        />
      </div>

      {/* Footer */}
      <div className="mt-auto w-full">
        <hr className="border-t border-gray-200 w-full" />

        <div className="flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
          
          <button
            onClick={handleUpdateUOM}
            className="px-2 py-1 bg-[#f7525a] text-white rounded-md flex items-center"
          >
            <span>Update</span>
            {isSaving && <Spinner />}
          </button>

          <button
            onClick={handleClose}
            disabled={isSaving}
            className={`px-2 py-1 rounded-md ${
              isSaving
                ? 'bg-gray-400 text-gray-500 cursor-not-allowed'
                : 'bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD]'
            }`}
          >
            Cancel
          </button>

        </div>
      </div>
    </div>
  </div>
);
};

export default EditUOMModal;